import { isKilogramTemporaryPriceItem } from './temporaryPriceCore';
import { formatPesoAmount } from './money';
import { resolvePriceProductFormatCode } from './clientType';

export const isDetailPromotionTarget = (target) => resolvePriceProductFormatCode(target?.format || target) === 'detalle';

export const PRICE_LIST_PROMOTION_TYPES = Object.freeze({
  UNIT_PRICE: 'unit_price',
  PACK: 'pack',
  WEIGHT_THRESHOLD: 'weight_threshold',
});

export const PRICE_LIST_PROMOTION_STATES = Object.freeze({
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  ENDED: 'ended',
  REPLACED: 'replaced',
  CANCELLED: 'cancelled',
  ARCHIVED: 'archived',
  IMPORTED: 'imported',
});

const SANTIAGO_TIME_ZONE = 'America/Santiago';
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const positiveInteger = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const nonNegativeInteger = (value, fallback = null) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

const dateTimeMs = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

export const getSantiagoDateKey = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SANTIAGO_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
};

export const normalizePromotionDateKey = (value) => {
  const raw = String(value || '').trim();
  if (DATE_KEY_PATTERN.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}T/.test(raw)) return raw.slice(0, 10);
  return raw ? getSantiagoDateKey(raw) : '';
};

export const normalizePriceListPromotion = (raw = {}) => {
  const type = Object.values(PRICE_LIST_PROMOTION_TYPES).includes(raw.type) ? raw.type : PRICE_LIST_PROMOTION_TYPES.UNIT_PRICE;
  const quantity = type === 'weight_threshold' ? Number(raw.quantity)
    : type === PRICE_LIST_PROMOTION_TYPES.PACK ? positiveInteger(raw.quantity ?? raw.packQuantity, 2) : 1;
  return {
    ...raw,
    id: positiveInteger(raw.id),
    batchId: raw.batchId ?? null,
    branchId: positiveInteger(raw.branchId),
    varietyPriceId: positiveInteger(raw.varietyPriceId),
    type,
    quantity,
    promotionPrice: nonNegativeInteger(
      raw.promotionPrice ?? raw.sellingPrice ?? raw.promoUnitPrice ?? raw.packPrice
    ),
    startDate: normalizePromotionDateKey(raw.startDate ?? raw.startsAt),
    endDate: normalizePromotionDateKey(raw.endDate ?? raw.expiresAt),
    startsAt: raw.startsAt || null,
    expiresAt: raw.expiresAt || null,
    state: String(raw.state || '').trim().toLowerCase(),
    target: raw.target && typeof raw.target === 'object' ? raw.target : null,
  };
};

export const normalizeFormatPromotions = (format = {}) => {
  const source = Array.isArray(format.promotions)
    ? format.promotions
    : format.promotion
      ? [format.promotion]
      : [];
  return source
    .map(normalizePriceListPromotion)
    .filter((promotion) => promotion.id && promotion.varietyPriceId)
    .sort((left, right) => (
      String(left.startDate).localeCompare(String(right.startDate))
      || Number(left.id) - Number(right.id)
    ));
};

export const getPromotionState = (promotion, now = Date.now()) => {
  const normalized = normalizePriceListPromotion(promotion);
  const storedState = normalized.state;
  if (storedState === PRICE_LIST_PROMOTION_STATES.ENDED) {
    const endedAtMs = dateTimeMs(normalized.endedAt);
    const startsAtMs = dateTimeMs(normalized.startsAt);
    if (normalized.endedReason === 'cancelled'
      || (Number.isFinite(endedAtMs) && Number.isFinite(startsAtMs) && endedAtMs < startsAtMs)) {
      return PRICE_LIST_PROMOTION_STATES.CANCELLED;
    }
  }
  if ([
    PRICE_LIST_PROMOTION_STATES.ENDED,
    PRICE_LIST_PROMOTION_STATES.REPLACED,
    PRICE_LIST_PROMOTION_STATES.EXPIRED,
    PRICE_LIST_PROMOTION_STATES.CANCELLED,
    PRICE_LIST_PROMOTION_STATES.ARCHIVED,
    PRICE_LIST_PROMOTION_STATES.IMPORTED,
  ].includes(storedState)) return storedState;

  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const startsAtMs = dateTimeMs(normalized.startsAt);
  const expiresAtMs = dateTimeMs(normalized.expiresAt);
  if (Number.isFinite(expiresAtMs) && nowMs >= expiresAtMs) {
    return PRICE_LIST_PROMOTION_STATES.EXPIRED;
  }
  if (Number.isFinite(startsAtMs) && nowMs < startsAtMs) {
    return PRICE_LIST_PROMOTION_STATES.SCHEDULED;
  }

  if (!normalized.startsAt || !normalized.expiresAt) {
    const today = getSantiagoDateKey(new Date(nowMs));
    if (normalized.endDate && today > normalized.endDate) {
      return PRICE_LIST_PROMOTION_STATES.EXPIRED;
    }
    if (normalized.startDate && today < normalized.startDate) {
      return PRICE_LIST_PROMOTION_STATES.SCHEDULED;
    }
  }
  return PRICE_LIST_PROMOTION_STATES.ACTIVE;
};

export const isPromotionCurrent = (promotion, now = Date.now()) => [
  PRICE_LIST_PROMOTION_STATES.ACTIVE,
  PRICE_LIST_PROMOTION_STATES.SCHEDULED,
].includes(getPromotionState(promotion, now));

export const getPromotionNextBoundaryMs = (promotions = [], now = Date.now()) => {
  const nowMs = now instanceof Date ? now.getTime() : Number(now);
  const candidates = (Array.isArray(promotions) ? promotions : []).flatMap((promotion) => {
    if (!isPromotionCurrent(promotion, nowMs)) return [];
    const normalized = normalizePriceListPromotion(promotion);
    return [dateTimeMs(normalized.startsAt), dateTimeMs(normalized.expiresAt)]
      .filter((value) => Number.isFinite(value) && value > nowMs);
  });
  return candidates.length ? Math.min(...candidates) : null;
};

export const collectCatalogPromotions = (products = []) => {
  const promotionsById = new Map();
  (Array.isArray(products) ? products : []).forEach((product) => {
    const varieties = product?.varieties || product?.Varieties || [];
    (Array.isArray(varieties) ? varieties : []).forEach((variety) => {
      const formats = variety?.formats || variety?.VarietyPrices || [];
      (Array.isArray(formats) ? formats : []).forEach((format) => {
        // Format-offer rows are transient overlays cloned from their source format.
        // They can be retained after expiry to keep an in-progress draft stable, so
        // collecting their cloned promotions would resurrect a row removed upstream.
        if (format?.isFormatOffer === true || positiveInteger(format?.formatOfferId)) return;
        const availability = format?.offerAvailability || format?.branch?.offerAvailability;
        if (availability && !availability.endedAt) {
          promotionsById.set(`offer-availability-${format.id}`, { ...availability, id: `offer-availability-${format.id}` });
        }
        normalizeFormatPromotions(format).forEach((promotion) => {
          promotionsById.set(String(promotion.id), promotion);
        });
      });
    });
  });
  return Array.from(promotionsById.values());
};

export const isWeightPromotionTarget = (target = {}) => isKilogramTemporaryPriceItem(target.format || target);

export const isDiscretePromotionTarget = (target = {}) => {
  const format = target?.format && typeof target.format === 'object'
    ? target.format
    : target;
  if (format?.useScaleWeight === true) return false;
  const metricUnit = String(format?.metricUnit || '').trim().toLowerCase();
  return !(
    metricUnit.includes('kg')
    || metricUnit.includes('kilo')
    || metricUnit.includes('gr')
    || metricUnit === 'g'
    || metricUnit.includes('lt')
    || metricUnit.includes('litro')
    || metricUnit === 'l'
  );
};

export const formatPromotionTerms = (promotion) => {
  const normalized = normalizePriceListPromotion(promotion);
  const price = normalized.promotionPrice == null
    ? 'Sin precio'
    : `$${formatPesoAmount(normalized.promotionPrice)}`;
  if (normalized.type === 'weight_threshold') return `${normalized.quantity.toLocaleString('es-CL')} kg por ${price} · precio/kg desde ese peso`;
  return normalized.type === PRICE_LIST_PROMOTION_TYPES.PACK
    ? `${normalized.quantity} por ${price}`
    : `${price} por unidad`;
};

export const formatPromotionDate = (value) => {
  const dateKey = normalizePromotionDateKey(value);
  if (!DATE_KEY_PATTERN.test(dateKey)) return 'Sin fecha';
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: SANTIAGO_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
};

export const buildPromotionPayload = ({
  varietyPriceIds,
  type,
  quantity,
  promotionPrice,
  startDate,
  endDate,
} = {}) => ({
  varietyPriceIds: Array.from(new Set((varietyPriceIds || [])
    .map((value) => positiveInteger(value))
    .filter(Boolean))),
  type: Object.values(PRICE_LIST_PROMOTION_TYPES).includes(type) ? type : PRICE_LIST_PROMOTION_TYPES.UNIT_PRICE,
  quantity: type === 'weight_threshold' ? Number(String(quantity).replace(',', '.')) : type === PRICE_LIST_PROMOTION_TYPES.PACK
    ? positiveInteger(quantity)
    : 1,
  promotionPrice: nonNegativeInteger(promotionPrice),
  startDate: normalizePromotionDateKey(startDate),
  endDate: normalizePromotionDateKey(endDate),
});
